/**
 * Spec 053, step 4: «Дежурство по методам» (`/knowledge/duty`) — what the server found in the
 * methods' base on Monday, without an LLM: methods close enough to be one, drafts nobody
 * applied, notes waiting too long, orders closed as routine. It only tells: merging is a new
 * version of a method, archiving is its status — the head of the unit decides, or a person here.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Experience, type DutyMethod } from "@/api/experience";
import { SkeletonList } from "@/components/ui/Skeleton";
import { ago, AppBar, Caption, EmptyNote, ScreenHeader } from "@/kit";

function MethodLink({ m }: { m: DutyMethod }) {
  return <Link to={`/knowledge/${m.id}`} className="font-semibold text-p-link">{m.title}</Link>;
}

function Card({ children }: { children: ReactNode }) {
  return <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">{children}</ul>;
}

function Row({ children }: { children: ReactNode }) {
  return <li className="border-b border-p-border px-4 py-3 text-body text-p-text last:border-0">{children}</li>;
}

export function MethodDutyScreen() {
  const duty = useQuery({ queryKey: ["method-duty"], queryFn: Experience.duty });
  const report = duty.data;
  const week = report ? new Date(report.week_start).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" }) : null;
  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/knowledge" context="Знания" />
      <ScreenHeader title="Дежурство по методам" sub={week ? `Неделя с ${week} · собрано ${ago(report!.created_at)}` : "Раз в неделю, в понедельник в 09:00"} />
      {duty.isLoading && <SkeletonList count={3} />}
      {duty.isError && <EmptyNote>Не удалось загрузить отчёт. Попробуй ещё раз.</EmptyNote>}
      {duty.isSuccess && !report && <EmptyNote>Отчёта ещё нет: первый соберётся в понедельник в 09:00.</EmptyNote>}
      {report && (
        <>
          <Caption>Похожие методы — слить новой версией?</Caption>
          {report.pairs.length ? (
            <Card>
              {report.pairs.map((p) => (
                <Row key={`${p.first.id}-${p.second.id}`}>
                  <MethodLink m={p.first} /> и <MethodLink m={p.second} />
                </Row>
              ))}
            </Card>
          ) : (
            <EmptyNote>Похожих методов нет.</EmptyNote>
          )}

          <Caption>Черновики старше 60 дней без применений — в архив?</Caption>
          {report.stale_drafts.length ? (
            <Card>
              {report.stale_drafts.map((m) => (
                <Row key={m.id}>
                  <MethodLink m={m} />
                  {m.created_at && <span className="text-meta text-p-text-muted"> · записан {ago(m.created_at)}</span>}
                </Row>
              ))}
            </Card>
          ) : (
            <EmptyNote>Забытых черновиков нет.</EmptyNote>
          )}

          <Caption>Заметки ждут разбора дольше 14 дней</Caption>
          {report.stuck_notes.length ? (
            <Card>
              {report.stuck_notes.map((n) => (
                <Row key={n.unit_id ?? "people"}>
                  {n.unit_id ? (
                    <span>{n.unit_name}</span>
                  ) : (
                    <Link to="/knowledge/experience" className="font-semibold text-p-link">Опыт не разобран</Link>
                  )}
                  <span className="text-meta text-p-text-muted"> · {n.count} · самая старая {ago(n.oldest)}</span>
                </Row>
              ))}
            </Card>
          ) : (
            <EmptyNote>Застрявших заметок нет.</EmptyNote>
          )}

          <Caption>Закрыто «рутиной» за неделю</Caption>
          {report.routine.length ? (
            <Card>
              {report.routine.map((r) => (
                <Row key={`${r.agent_id}-${r.unit_id}`}>
                  {r.agent_name} <span className="text-meta text-p-text-muted">· {r.unit_name} · {r.count}</span>
                </Row>
              ))}
            </Card>
          ) : (
            <EmptyNote>«Рутиной» за неделю не закрывали.</EmptyNote>
          )}
        </>
      )}
    </div>
  );
}
